import { Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, share, switchMap, withLatestFrom } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { InstalledApplication } from '@rocksoft/mdm-sdk-v2';

import { AsyncPipe } from '@angular/common';
import { RequestType } from '@era/locations-sdk';
import { AppleDeviceProfileResolverActions, selectProfileItemByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { PanelComponent, PanelFooterComponent, SkSpinner3Component } from '@rocksoft/cdk';
import { isNotNullOrUndefined } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';
import { AppLockModalComponent } from '../app-lock-modal/app-lock-modal.component';
import { AppleAppComponent } from '../app/apple-app.component';

@Component({
  selector: 'ml-apple-app-view',
  templateUrl: './apple-app.view.html',
  imports: [
    PanelComponent,
    SkSpinner3Component,
    AppleAppComponent,
    PanelFooterComponent,
    CommandSpinnerComponent,
    FaIconComponent,
    AsyncPipe,
  ],
})
export class AppleAppViewComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly modalService = inject(NgbModal);
  private readonly installedApplicationsStore = inject(Store);

  private _removeAppProgress: Subject<CommandProgress> = new Subject<CommandProgress>();

  readonly removeAppProgress$: Observable<CommandProgress>;

  readonly app$: Observable<InstalledApplication>;
  readonly udid$: Observable<string>;
  readonly bundleId$: Observable<string>;

  static navigationHandledCommands = new Set<string>();

  private removeAppSubscription: Subscription;

  constructor() {
    const constructorCalledAt = new Date();

    const parentRoute = this.route.parent;
    const grandParentRoute = parentRoute?.parent;

    if (!parentRoute || !grandParentRoute) {
      throw new Error('Expected route to have both parent and grandparent routes.');
    }

    this.udid$ = grandParentRoute.paramMap.pipe(
      map((p) => p.get('udid')),
      filter(isNotNullOrUndefined)
    );

    this.bundleId$ = this.route.paramMap.pipe(
      map((p) => p.get('bundleId')),
      filter(isNotNullOrUndefined)
    );
    this.app$ = this.bundleId$.pipe(
      switchMap((bundleId) =>
        this.installedApplicationsStore.select(selectProfileItemByType('mdm-installed-application', bundleId))
      ),
      filter(isNotNullOrUndefined)
    );

    this.removeAppProgress$ = this.udid$.pipe(
      switchMap((udid) =>
        merge(
          this._removeAppProgress,
          this.commandProgressService.commandsOfRequestType(udid, 'RemoveApplication')
        ).pipe(share())
      )
    );

    this.removeAppSubscription = this.removeAppProgress$
      .pipe(
        // Make sure the command progress is for the current device
        withLatestFrom(this.udid$),
        filter(([cp, udid]) => cp.udid.trim().toLowerCase() == udid.trim().toLowerCase()),
        // We only want to handle the case where the command completed
        filter(([cp]) => cp.status === 'Acknowledged'),
        // Make sure this is not some old command, but one created as a result of this page
        filter(([cp]) => {
          const val = cp.createdAt == undefined || cp.createdAt.valueOf() > constructorCalledAt.valueOf();
          return val;
        }),
        // We only want to navigate for each command once, globally
        filter(([cp]) => {
          const commandUuid = cp.commandUUID.trim().toLowerCase();
          if (AppleAppViewComponent.navigationHandledCommands.has(commandUuid)) {
            return false;
          }

          AppleAppViewComponent.navigationHandledCommands.add(commandUuid);
          return true;
        })
      )
      .subscribe(([, udid]) => {
        // Navigate back, since the current application is no longer available
        void this.router.navigate([], { relativeTo: this.route.parent });

        // Reload applications
        this.installedApplicationsStore.dispatch(
          AppleDeviceProfileResolverActions.profileRequested({
            udid,
            profileType: 'mdm-installed-application',
          })
        );
      });
  }

  ngOnDestroy(): void {
    if (this.removeAppSubscription) {
      this.removeAppSubscription.unsubscribe();
    }
  }

  public showAppLockModal(app: InstalledApplication, udid: string) {
    const modal = this.modalService.open(AppLockModalComponent, { scrollable: true, size: 'xl' });
    modal.componentInstance.udid = udid;
    modal.componentInstance.app = app;
  }

  public remove(udid: string, app: InstalledApplication): void {
    this._removeAppProgress.next(this.createInitialCommandProgress(udid, 'RemoveApplication'));

    this.mdmService.removeApp(udid, app.identifier ?? '').subscribe(); // Identifier can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
  }

  private createInitialCommandProgress(udid: string, requestType: RequestType): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType,
    };
  }
}
